import { useEffect, useRef } from "react";

interface VoiceWaveVisualizerProps {
  stream?: MediaStream | null;
  height?: number;
  barColor?: string;
  barCount?: number;
  idle?: boolean;
  idleOpacity?: number;
}

// Convert hex color to rgba with opacity
const hexToRgba = (hex: string, opacity: number): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const r = Number.parseInt(result[1], 16);
    const g = Number.parseInt(result[2], 16);
    const b = Number.parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return hex;
};

export const VoiceWaveVisualizer: React.FC<VoiceWaveVisualizerProps> = ({
  stream,
  height = 60,
  barColor = "#1677ff",
  barCount = 50,
  idle = false,
  idleOpacity = 0.25,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();

  // Draw idle/placeholder state
  useEffect(() => {
    if (!idle || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const drawIdle = () => {
      const width = canvas.offsetWidth;
      canvas.width = width;

      ctx.clearRect(0, 0, width, height);

      const barWidth = width / barCount;
      const gap = 2;

      for (let i = 0; i < barCount; i++) {
        // Create a gentle wave pattern
        const waveHeight = Math.sin((i / barCount) * Math.PI * 3) * 0.3 + 0.4;
        const barHeight = waveHeight * height * 0.5 + 4;

        const x = i * barWidth;
        const y = (height - barHeight) / 2;

        ctx.fillStyle = hexToRgba(barColor, idleOpacity);
        ctx.beginPath();
        ctx.roundRect(x + gap / 2, y, barWidth - gap, barHeight, 3);
        ctx.fill();
      }
    };

    drawIdle();

    // Redraw on resize
    const resizeObserver = new ResizeObserver(drawIdle);
    resizeObserver.observe(canvas);

    return () => resizeObserver.disconnect();
  }, [idle, height, barColor, idleOpacity, barCount]);

  // Draw active recording state
  useEffect(() => {
    if (idle || !stream || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.8; // Smooth transitions between frames

    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    // Store previous bar heights for smooth animation
    const barHeights = new Array(barCount).fill(2);

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);

      const width = canvas.offsetWidth;
      canvas.width = width;

      analyser.getByteFrequencyData(dataArray);
      ctx.clearRect(0, 0, width, height);

      const barWidth = width / barCount;
      const gap = 2;

      // Calculate average volume for overall sensitivity
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += dataArray[i];
      }
      const average = sum / bufferLength;
      const sensitivity = Math.max(1, average / 30);

      for (let i = 0; i < barCount; i++) {
        // Sample from voice frequency range (spread across spectrum)
        const startIndex = Math.floor((i / barCount) * bufferLength * 0.6);
        const endIndex = Math.floor(((i + 1) / barCount) * bufferLength * 0.6);

        // Average multiple frequency bins per bar
        let barSum = 0;
        for (let j = startIndex; j < endIndex; j++) {
          barSum += dataArray[j];
        }
        const barAvg = barSum / Math.max(1, endIndex - startIndex);

        // Reduce low frequency bars (beginning) to balance visualization
        const position = i / barCount;
        const lowFreqCompensation = 0.6 + position * 0.4; // 0.6 at start, 1.0 at end

        // Target height with sensitivity boost and compensation
        const targetHeight =
          Math.min((barAvg / 255) * sensitivity * lowFreqCompensation, 1) *
            height *
            0.85 +
          4;

        // Smooth transition to target
        barHeights[i] += (targetHeight - barHeights[i]) * 0.3;

        const x = i * barWidth;
        const y = (height - barHeights[i]) / 2;

        ctx.fillStyle = barColor;
        ctx.beginPath();
        ctx.roundRect(x + gap / 2, y, barWidth - gap, barHeights[i], 3);
        ctx.fill();
      }
    };

    draw();

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      void audioContext.close();
    };
  }, [stream, height, barColor, barCount]);

  return (
    <canvas
      ref={canvasRef}
      height={height}
      style={{ width: "100%", display: "block" }}
    />
  );
};
